import { useState } from "react";
import { Link } from "react-router-dom";
import { Gift, Instagram, Send, Mail, Phone } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";

const COLS = [
  {
    title: "Customize Gift Online",
    links: [["About Us", "/about"], ["Contact Us", "/contact"], ["Careers", "/careers"], ["Blog", "/blog"]],
  },
  {
    title: "Customer Support",
    links: [["Help Center", "/faq"], ["Shipping", "/shipping"], ["Returns", "/returns-refunds"], ["Cancellation", "/returns-refunds"], ["Track Order", "/track-order"]],
  },
  {
    title: "Policies",
    links: [["Privacy Policy", "/policies"], ["Terms & Conditions", "/policies"], ["Refund Policy", "/returns-refunds"], ["Shipping Policy", "/shipping"]],
  },
];

export default function Footer() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/newsletter/subscribe", { email });
      toast.success(data.message);
      setEmail("");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <footer className="bg-[#1C1917] text-stone-300 mt-24" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <Link to="/" className="flex items-center gap-2">
              <span className="w-9 h-9 rounded-xl bg-[#B45309] text-[#FDFBF7] grid place-items-center"><Gift size={18} /></span>
              <span className="font-serif text-xl font-bold text-[#FDFBF7]">Customize Gift <span className="text-[#E0A96D]">Online</span></span>
            </Link>
            <p className="mt-4 text-sm text-stone-400 leading-relaxed max-w-xs">Personalised mugs, photo frames, cushions and keepsakes — crafted with your photos and words, delivered across India.</p>
            <form onSubmit={subscribe} className="mt-6 flex gap-2" data-testid="newsletter-form">
              <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="Your email for sweet deals" className="flex-1 h-11 px-4 rounded-full bg-stone-800 border border-stone-700 text-sm outline-none focus:border-[#E0A96D] placeholder:text-stone-500" data-testid="newsletter-input" />
              <button disabled={busy} className="h-11 px-4 rounded-full bg-[#B45309] hover:bg-[#92400E] text-white transition-colors disabled:opacity-60" aria-label="Subscribe" data-testid="newsletter-submit">
                <Send size={16} />
              </button>
            </form>
            <div className="mt-6 space-y-2.5">
              <a href="https://www.instagram.com/customizegiftonline.com126" target="_blank" rel="noreferrer" className="flex items-center gap-2.5 text-sm text-stone-400 hover:text-[#FDFBF7] transition-colors" data-testid="social-instagram">
                <span className="w-8 h-8 rounded-full bg-stone-800 grid place-items-center"><Instagram size={14} /></span>
                Instagram — customizegiftonline.com126
              </a>
              <p className="flex items-center gap-2.5 text-sm text-stone-400" data-testid="social-facebook">
                <span className="w-8 h-8 rounded-full bg-stone-800 grid place-items-center text-[11px] font-bold">f</span>
                Facebook — Customize Gift
              </p>
            </div>
          </div>
          {COLS.map((col) => (
            <div key={col.title} className="md:col-span-2">
              <h4 className="text-xs font-bold uppercase tracking-[0.18em] text-[#E0A96D] mb-4">{col.title}</h4>
              <ul className="space-y-2.5">
                {col.links.map(([label, to]) => (
                  <li key={label}>
                    <Link to={to} className="text-sm text-stone-400 hover:text-[#FDFBF7] transition-colors" data-testid={`footer-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{label}</Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
          <div className="md:col-span-2">
            <h4 className="text-xs font-bold uppercase tracking-[0.18em] text-[#E0A96D] mb-4">Need Help?</h4>
            <p className="text-sm text-stone-400">Mon–Sat, 9am–9pm IST</p>
            <a href="mailto:customizegiftonline@gmail.com" className="flex items-center gap-1.5 text-sm text-stone-400 mt-2 hover:text-[#FDFBF7] transition-colors break-all" data-testid="footer-email"><Mail size={13} className="shrink-0" /> customizegiftonline@gmail.com</a>
            <a href="tel:9899534126" className="flex items-center gap-1.5 text-sm text-stone-400 mt-2 hover:text-[#FDFBF7] transition-colors" data-testid="footer-phone"><Phone size={13} className="shrink-0" /> 9899534126</a>
            <Link to="/contact" className="inline-block mt-4 text-sm font-semibold text-[#FDFBF7] underline underline-offset-4 decoration-[#B45309] hover:text-[#E0A96D] transition-colors" data-testid="footer-support-link">Contact Support</Link>
          </div>
        </div>
        <div className="mt-14 pt-6 border-t border-stone-800 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-stone-500">© {new Date().getFullYear()} Customize Gift Online. All rights reserved.</p>
          <p className="text-xs text-stone-500">Secure payments · UPI · Cards · Net Banking · COD</p>
        </div>
      </div>
    </footer>
  );
}
